import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Eye, Award } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const { language, t } = useLanguage();

  const seoData = {
    es: {
      title: 'Sobre Nosotros | PASTORIA SERRALTA - Promotor Inmobiliario Baleares',
      description: 'Conozca PASTORIA SERRALTA, S.L. - Promotor inmobiliario fundado en 2018 en las Islas Baleares. Nuestra visión, misión y compromiso con viviendas de calidad.'
    },
    en: {
      title: 'About Us | PASTORIA SERRALTA - Real Estate Developer Balearic Islands',
      description: 'Learn about PASTORIA SERRALTA, S.L. - Real estate developer founded in 2018 in the Balearic Islands. Our vision, mission and commitment to quality homes.'
    }
  };

  const sections = [
    {
      icon: Award,
      title: t.about.whoWeAre.title,
      content: t.about.whoWeAre.text,
      color: '#356C8A'
    },
    {
      icon: Eye,
      title: t.about.vision.title,
      content: t.about.vision.text,
      color: '#2B7FD9'
    },
    {
      icon: Target,
      title: t.about.mission.title,
      content: t.about.mission.text,
      color: '#356C8A'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO 
        title={seoData[language].title}
        description={seoData[language].description}
        url={language === 'es' ? '/sobre-nosotros' : '/en/about'}
      />
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/image30.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#356C8A]/80" />
        </div>
        
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl sm:text-6xl font-bold" data-testid="about-title">
            {t.about.title}
          </h1>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <div 
                  key={index} 
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } gap-8 items-center`}
                  data-testid={`about-section-${index}`}
                >
                  <div className="flex-1">
                    <div 
                      className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${section.color}20` }}
                    >
                      <Icon className="h-12 w-12" style={{ color: section.color }} />
                    </div>
                    <h2 className="text-3xl font-bold text-[#356C8A] mb-4">
                      {section.title}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={`/image${12 + index * 6}.jpeg`}
                        alt={section.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#E8F2F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { label: '2018', desc: { es: 'Año de fundación', en: 'Year founded' } },
              { label: '100%', desc: { es: 'Compromiso con la calidad', en: 'Commitment to quality' } },
              { label: '5+', desc: { es: 'Promociones realizadas', en: 'Projects completed' } }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-8 bg-white rounded-xl shadow-md" data-testid={`stat-${idx}`}>
                <div className="text-5xl font-bold text-[#356C8A] mb-2">
                  {stat.label}
                </div>
                <div className="text-gray-600">
                  {stat.desc[language]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;